import { createSlice } from '@reduxjs/toolkit'
import { useCategories } from 'state/Categories'
import { useDeepMemo, useObjSelector, useObjSelectorOrFetch } from 'utils'
import API from './API'
import MenuItem from './models/MenuItem'
import ItemIcon from './models/ItemIcon'

const initialState = {
	list      	 : [],
	currentItem	 : null,
	icons      	 : [],
	wildCardState: false
}

const slice = createSlice({
	name: 'items',
	initialState,
	reducers     			: {
		load       			: (state, { payload: items }) => { Object.assign(state, { list: items }) },
		clearItems 			: state => initialState,
		clearCurrentItem: state => { state.currentItem = null },
		updateListItem  : (state, { payload: { id, ...fields } }) => {
			const item = state.list.find(item => item.id == id)
			if (!item) {
				console.warn(`Item #${id} to update was not found? Total of items in state: ${state.items.length}`)
				return
			}
			Object.assign(item, fields)
		},
		setCurrentItem	: (state, { payload }) => { state.currentItem = payload },
		setItemIcons    : (state, { payload }) => { state.icons = payload },
		setWildcardState: (state, { payload }) => { state.wildcardState = payload },
		updateCurrentImage: (state, { payload }) => { state.currentItem.image = payload }
	},
})

//not exactly official, but enough for some organization
slice.thunks = {
	fetchItems: async dispatch => {
		const resp = await API.get('item')
		dispatch(slice.actions.load(resp.data.items))
	},
	fetchItem: id => async dispatch => {
		const resp = await API.get(`item/${id}`)
		dispatch(slice.actions.setCurrentItem(resp.data))
	},
	toggleItem: (item, shown_status) => dispatch => {
		let old_status = item.shown_status

		dispatch(slice.actions.updateListItem({ id: item.id, shown_status }))
		return API.patch(`item/${item.id}/${shown_status}`)
			.catch(() => dispatch(slice.actions.updateListItem({ id: item.id, shown_status: old_status })))
	},
	fetchIcons: async dispatch => {
		const resp = await API.get(`item/icons`)
		dispatch(slice.actions.setItemIcons(resp.data))
	},
	editItem: (id, item) => async (dispatch, getState) => {
		return API.patch(`item/${id}`, item)
			.then(async (resp) => {
				await dispatch(slice.actions.setCurrentItem(resp.data))
				const updatedItem = getCurrentItem(getState())
				dispatch(slice.actions.updateListItem({ id, display_name: updatedItem.display_name }))
				return updatedItem
			})
			.catch(e => {
			console.error(e)
			return false
		}) //TODO what happens if there's some error on the endpoint?
	},
	//This guy needs to exist to fix a bug RHF creates on Item.js (See Items/Item.js:68)
	editWildcardState: (status) => async (dispatch) => {
		await dispatch(slice.actions.setWildcardState(status))
		setTimeout(async () => {
			await dispatch(slice.actions.setWildcardState(false))	
		}, 1000)
		return true
	},
	uploadImage: (id, img) => async (dispatch) => {
		const url = await API.post(`item/${id}/image`, { imgData: img })
		await dispatch(slice.actions.updateCurrentImage(url.data))
		return url
	},
	deleteImage: id => async (dispatch) => {
		await API.delete(`item/${id}/image`)
		await dispatch(slice.actions.updateCurrentImage(''))
	}
}

// noinspection JSValidateTypes
/**
 * Use only with the direct state at hand. Otherwise, go with {@link useItems()}, which is reload-safe.
 * @param state {{ items: object[] }}
 * @returns {MenuItem[]}
 * @see useItems
 */
export const getItems = ({ items }) => MenuItem.map(items.list)

/**
 * Selects the current items from the state with a hook.
 * If that's empty, tries to request them from the API.
 * @returns {MenuItem[]}
 */
export const useItems = () => useObjSelectorOrFetch(getItems, fetchItems, slice.name)

/**
 * Groups Items in their respective Categories, and fetches both if they're not yet available.
 * @return {Array<{ category: Category, items: MenuItem[] }>} Items grouped by categories
 */
export const useGroupedItemsByCategory = () => {
	const items      = useItems()
	const categories = useCategories()
	// console.log({ items, categories })
	return useDeepMemo(() => {
		if (!categories.length) { //might be empty in case of a race condition or... some odd menu configuration??
			console.debug('Cannot group items yet, categories seems to not have loaded!')
			return []
		}

		//we can't really just use an object "list" (_.groupBy) because Firefox has a different ordering :roll_eyes:
		const groups   = []
		const hasGroup = []
		for (let item of items) {
			const groupIndex = hasGroup.indexOf(item.category_id)
			if (groupIndex == -1) {
				hasGroup.push(item.category_id)
				groups.push({
					category: categories.find(c => c.id == item.category_id),
					items   : [item],
				})
			} else {
				groups[groupIndex].items.push(item)
			}
		}

		return groups
	}, [items, categories])
}

export const getCurrentItem = ({ items }) => items.currentItem ? new MenuItem(items.currentItem) : null

export const useItemIcons = () => useObjSelector(({ items }) => ItemIcon.map(items.icons))

export const {
	fetchItems, toggleItem, fetchItem,
	editItem, editWildcardState, deleteImage, uploadImage
} = slice.thunks
export const { clearItems, setItemIcons } = slice.actions
export default slice.reducer
